import {
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  HostListener,
  Input,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { filter, of, take } from 'rxjs';
import { collapseExpand } from '../../animations/animations';
import { SelectOption } from '@rx-applications/shared/utils';

@Component({
  selector: 'rx-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent), //tslint:disable-line
      multi: true,
    },
  ],
  animations: [collapseExpand],
})
export class SelectComponent implements ControlValueAccessor {
  #options: any[];

  /** Dropdown options JSON object */
  @Input() set options(options: any[]) {
    this.#options = options;
    this.optionsToDisplay = options;
    this.writeValue(this.value);
  }

  get options(): any[] {
    return this.#options;
  }

  multipleOptionsSelected = signal<SelectOption[]>([]);

  multipleSelectedOptionsLabel = computed(() => {
    const optionsSelected = this.multipleOptionsSelected();
    if (!optionsSelected.length) {
      return '-';
    }
    const [{ label, secondaryLabel, value }] = optionsSelected;
    const firstOption = label || secondaryLabel || String(value);
    const trimmedOption = firstOption.length > 21 ? `${firstOption.substring(0, 21)}...` : firstOption;
    return optionsSelected.length === 1 ? trimmedOption : `${trimmedOption}, + ${optionsSelected.length - 1}`;
  });

  /** Enable grouped options with headings */
  @Input() group = false;

  /** Placeholder text */
  @Input() placeholder: string;

  /** Unique identifier */
  @Input() selectId?: string;

  /** Is required */
  @Input() required: boolean;

  /** Native HTML attribute */
  @Input() name: string;

  /** Label text */
  @Input() label?: string;

  /** Secodnary label text */
  @Input() secondaryLabel?: string;

  /** Enable filter search of the dropdown list */
  @Input() filter = false;

  /** Set predefined input value */
  @Input() filterValue?: string;

  /** No result message for filter search */
  @Input() filterNoResultMessage?: string;

  /** Display label value e.g Test Internal (1216)*/
  @Input() displayLabelValue = false;

  /** Determines whether the dropdown position is calculated dynamically */
  @Input() dynamicPosition = false;

  /** Adjustment value for showing the dropdown above, in case of an obstructed view */
  @Input() breakpointAdjustment = 0;

  /** Show dropdown footer to project a component */
  @Input() showFooter = false;

  /** Enable Multiselect dropdown */
  @Input() multiSelect = false;

  /** Text to show in the search input when multiselect and filter is enabled */
  @Input() searchText: string = 'Search';

  /** Label to show when the option is empty */
  @Input() emptyOptionLabel: string = 'empty';

  /**Emit an event with the selected option */
  @Output() selectItemChange? = new EventEmitter<SelectOption | SelectOption[]>();

  /** Input HTML element reference */
  @ViewChild('filter', { read: ElementRef }) filterViewChild: ElementRef;

  /** Compact version */
  @HostBinding('class.compact') @Input() compact: boolean;

  /** Disable input */
  @HostBinding('class.disabled') @Input() disabled: boolean;

  /** Readonly input */
  @HostBinding('class.readonly') @Input() readonly: boolean;

  /** Invalid input */
  @HostBinding('class.invalid') @Input() error: string;

  /** Quick search state object */
  public state = {
    buffer: [],
    lastKeyTime: Date.now(),
  };

  /** Dropdown opened state */
  isDropdownOpen = false;

  /** Focused state */
  focused: boolean;

  /** Input value */
  value: string | number;

  /** Options changed */
  optionsChanged: boolean;

  /** Options to display */
  optionsToDisplay: SelectOption[];

  /** Selected option */
  selectedOption: SelectOption;

  /** Input field value updated (to the selected one from dropdown menu) */
  selectedOptionUpdated: boolean;

  readonly idealVisibleItems = 8;

  onModelTouched = () => {
    /**/
  };

  constructor(
    private cd: ChangeDetectorRef,
    private elementRef: ElementRef,
  ) {}

  get selectedOptionLabel(): string {
    return this.selectedOption ? this.selectedOption.label : null;
  }

  @HostListener('focusout', ['$event']) handleFocusOut(): void {
    of(this.isDropdownOpen)
      .pipe(
        filter((isDropdownOpen) => !isDropdownOpen),
        take(1),
      )
      .subscribe(() => {
        if (!this.selectedOption) {
          this.resetFilter();
        } else if (this.selectedOption && this.filterValue && this.filterValue !== this.selectedOption.label) {
          this.resetFilter();
          this.selectItem(null);
        }
        this.cd.markForCheck();
      });
  }

  @HostListener('document:keypress', ['$event'])

  /** Listen to keyboard typing / simulate native "select" option search from dropdown (quick search) */
  handleKeyboardEvent(event: KeyboardEvent) {
    if (this.isDropdownOpen && !this.filter) {
      const key = event.key;
      const currentTime = Date.now();
      let buffer = [];

      if (currentTime - this.state.lastKeyTime > 1000) {
        buffer = [key];
      } else {
        buffer = [...this.state.buffer, key];
      }

      this.state = { buffer: buffer, lastKeyTime: currentTime };

      const query = buffer.join('');

      setTimeout(() => {
        this.matchOption(query);
      }, 300);
    }
  }

  trackByOptions(index, { value }) {
    return value;
  }

  /** Compare keyboard strokes to dropdown list items */
  public matchOption(query) {
    let result, value;
    const optionsLength = this.group ? this.#options[0].items.length : this.#options.length;

    for (let index = 0; index < optionsLength; ++index) {
      value = this.group ? this.#options[0].items[index] : this.#options[index];
      if (value.label.toLowerCase().substring(0, query.length) === query.toLowerCase()) {
        // Found it, the full text is in `value`.
        // So you might grab it and break the loop, although
        // really what you do having found it depends on
        // what you need.
        result = value;
        break;
      }
    }

    if (result) {
      const el = document.getElementsByClassName('dropdown-item active');
      if (el) {
        this.selectItem(result);
        setTimeout(() => {
          this.updateSelectedOption(result.value);
          this.cd.markForCheck();
          el[0].scrollIntoView();
        }, 100);
      }
    }
  }

  public onChange = (value: any) => {
    /**/
  };
  public registerOnTouched = (fn): void => {
    this.onModelTouched = fn;
  };

  public registerOnChange = (fn: (val: any) => void) => (this.onChange = fn);
  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /** Update input field value */
  writeValue(value: any): void {
    if (this.filter) {
      this.resetFilter();
    }

    this.value = value;
    this.updateSelectedOption(value);

    if (this.multiSelect && Array.isArray(value)) {
      this.setInitialMultiSelectOption(value);
    }

    if (this.selectedOption) {
      this.selectItemChange.emit(this.selectedOption);
    }
    this.cd.markForCheck();
  }

  /** Reset filter */
  resetFilter(): void {
    if (this.filterViewChild && this.filterViewChild.nativeElement) {
      this.filterValue = null;
      this.filterViewChild.nativeElement.value = '';
    }

    this.optionsToDisplay = this.#options;
  }

  /** Set input field text */
  updateSelectedOption(val: any): void {
    this.selectedOption = this.findOption(val, this.optionsToDisplay);
    if (!this.placeholder && !this.selectedOption && this.optionsToDisplay && this.optionsToDisplay.length) {
      // tslint:disable-line
      this.selectedOption = this.optionsToDisplay[0];
    }
    this.filterValue = this.selectedOption?.label;
    this.selectedOptionUpdated = true;
  }

  setInitialMultiSelectOption(fieldValues: string[]): void {
    this.filterValue = null;
    const selectedOptions = this.optionsToDisplay.filter(({ value }) => fieldValues.includes(String(value)));
    this.multipleOptionsSelected.set(selectedOptions);
  }

  /** Click outside of dropdown */
  public handleClickOutsideDefaultDropdown() {
    this.hide();
  }

  /** On focus */
  onInputFocus(event) {
    this.focused = true;
  }

  /** On un-focus */
  onInputBlur(event) {
    this.focused = false;
    this.onModelTouched();
    this.filterValue = null;
  }

  /** On mouse click */
  onMouseclick(event) {
    if (this.disabled || this.readonly) {
      return;
    }

    const target = event.target;
    if (target.tagName === 'LI' || target.tagName === 'OPTION') {
      return this.hide();
    }
    if (!this.isDropdownOpen) {
      return this.show();
    }
  }

  /** Show dropdown menu */
  show() {
    this.isDropdownOpen = true;
    this.sortMultiSelectOptions();
  }

  /** Hide dropdown menu */
  hide() {
    this.isDropdownOpen = false;
    this.resetMultiSelectFilter();
  }

  /** Reset filter when multiselect is enabled */
  private resetMultiSelectFilter() {
    if (!this.multiSelect) {
      return;
    }
    this.filterValue = null;
    this.optionsToDisplay = this.#options;
  }

  private sortMultiSelectOptions() {
    if (!this.multiSelect) {
      return;
    }
    const selectedOptions = this.multipleOptionsSelected().sort((a, b) =>
      a.label && b.label ? a.label.localeCompare(b.label) : String(a.value).localeCompare(String(b.value)),
    );
    const noSelectedOptions = this.optionsToDisplay.filter((option) => !selectedOptions.includes(option));
    this.optionsToDisplay = [...selectedOptions, ...noSelectedOptions];
  }

  /** On dropdown item click */
  onItemClick(event, option) {
    if (!option.disabled) {
      this.selectItem(option);
    }

    setTimeout(() => {
      this.hide();
    }, 150);
  }

  onMultiSelectItem(option: SelectOption): void {
    this.toggleMultiSelectItem(option);
    this.onChange(this.multipleOptionsSelected());
    this.selectItemChange.emit(this.multipleOptionsSelected());
  }

  isMultiSelectItemSelected(option: SelectOption): boolean {
    return this.multipleOptionsSelected().includes(option);
  }

  private toggleMultiSelectItem(option: SelectOption): void {
    const isSelected = this.isMultiSelectItemSelected(option);
    if (isSelected) {
      this.multipleOptionsSelected.update((options) => options.filter((o) => o !== option));
    } else {
      this.multipleOptionsSelected.update((options) => [...options, option]);
    }
  }

  /** On option selection */
  selectItem(option: SelectOption) {
    if (this.selectedOption !== option) {
      this.selectedOption = option;
      this.value = option?.value;
      this.filterValue = this.filter && option?.label;
      this.onChange(this.value);
      this.selectItemChange.emit(option);
    }
  }

  /** Match item from search in dropdown */
  findOption(val: string, opts: any[], inGroup?: boolean): SelectOption {
    if (this.group && !inGroup) {
      let opt: SelectOption;
      if (opts && opts.length) {
        for (const optgroup of opts) {
          opt = this.findOption(val, optgroup.items, true);
          if (opt) {
            break;
          }
        }
      }
      return opt;
    } else {
      return opts?.find((i) => i.value === val);
    }
  }

  /** Filter search trigger */
  onFilter(event): void {
    const inputValue = event.target.value;
    if (inputValue && inputValue.length) {
      this.filterValue = inputValue;
      this.activateFilter();
    } else {
      this.filterValue = null;
      this.optionsToDisplay = this.#options;
      this.sortMultiSelectOptions();
    }

    this.optionsChanged = true;
  }

  /** Filter search activate, display filtered dropdown menu items */
  activateFilter() {
    if (this.#options && this.#options.length) {
      if (this.group) {
        const filteredGroups = [];
        for (const optgroup of this.#options) {
          const filteredSubOptions = this.doFilter(optgroup.items, this.filterValue);
          if (filteredSubOptions && filteredSubOptions.length) {
            filteredGroups.push({
              label: optgroup.label,
              value: optgroup.value,
              items: filteredSubOptions,
            });
          }
        }
        this.optionsToDisplay = filteredGroups;
      } else {
        this.optionsToDisplay = this.doFilter(this.#options, this.filterValue);
      }
      this.optionsChanged = true;
    }
  }

  /** Compare given value with items in list (filter) */
  doFilter(value: any[], filterValue: string) {
    const filteredItems: any[] = [];
    const filterText = filterValue.toLowerCase();

    if (value) {
      for (const item of value) {
        const fieldLabel = item['label']?.toLowerCase();
        const fieldSecondaryLabel = item['secondaryLabel']?.toLowerCase();
        const fieldValue = item['value'] ? String(item['value']).toLowerCase() : '';

        const matchLabel = fieldLabel?.indexOf(filterText) > -1;
        const matchSecondaryLabel = fieldSecondaryLabel?.indexOf(filterText) > -1;
        const matchValue = this.displayLabelValue && fieldValue.indexOf(filterText) > -1;

        if (matchLabel || matchSecondaryLabel || matchValue) {
          filteredItems.push(item);
        }
      }
    }

    return filteredItems;
  }

  get itemHeight(): number {
    return this.multiSelect ? 50 : 35;
  }

  get miniumHeight(): string {
    return this.filterNoResultMessage ? '40px' : '16px';
  }

  get searchFieldHeight(): number {
    return this.multiSelect && this.filter ? 90 : 0;
  }

  get virtualScrollHeight(): string {
    return `max(${this.miniumHeight}, ${this.dropdownHeight}px)`;
  }

  get dropdownPosition(): string {
    const halfWindowHeight = window.innerHeight / 2;
    const dropdownHeight = this.dropdownHeight;

    const noSpaceToFitDropdown =
      this.elementRef.nativeElement.getBoundingClientRect().bottom + this.breakpointAdjustment >
      halfWindowHeight + dropdownHeight;

    if (this.dynamicPosition && noSpaceToFitDropdown) {
      const yValue = `${-dropdownHeight - 50}px`;
      return `translateY(${yValue})`;
    }

    return `translateY(2px)`;
  }

  get footerPosition(): string {
    return `transform: translateY(max(${this.miniumHeight}, ${this.dropdownHeight + 2}px))`;
  }

  get dropdownHeight(): number {
    const { top, height } = this.elementRef.nativeElement.getBoundingClientRect();
    const availableSpace = window.innerHeight - top - height - this.searchFieldHeight * 2;
    const maxVisibleItems = Math.min(Math.floor(availableSpace / this.itemHeight), this.idealVisibleItems);
    const totalItems = this.optionsToDisplay?.length || 0;
    const visibleItems = Math.max(Math.min(maxVisibleItems, totalItems), 1);
    
    const listHeight = visibleItems * this.itemHeight;
    const safePadding = 16;
    return listHeight + safePadding;
  }

  get virtualScrollBufferSize(): { min: number; max: number } {
    const numberOfItems = this.optionsToDisplay?.length || 0;
    const minBufferPx = numberOfItems > this.idealVisibleItems ? this.idealVisibleItems * this.itemHeight : 200;
    const maxBufferPx = minBufferPx + 100;
    return { min: minBufferPx, max: maxBufferPx };
  }
}
